import Pusher from 'pusher-js'

/**
 * Soketi is fronted by TLS on 443. Any environment that does not declare a port
 * explicitly is assumed to be behind that same terminator — never NaN, which is
 * what an unparsed REACT_APP_API_PUSHER_PORT used to yield.
 */
const DEFAULT_WS_PORT = 443
const DEFAULT_CLUSTER = 'ap1'

/**
 * `Number` rather than `parseInt`, so a partial parse is rejected outright
 * ('6001abc') instead of silently accepted, and `Number.isInteger` rather than a
 * truthiness check, so fractions ('60.5') and negatives ('-1') fall back too.
 */
const resolvePort = (): number => {
  const port = Number(process.env.REACT_APP_API_PUSHER_PORT)
  return Number.isInteger(port) && port > 0 ? port : DEFAULT_WS_PORT
}

/**
 * Warn once per page load, not once per client — six components build clients on
 * a single screen. Deliberately not evaluated at module scope: an import-time
 * side effect would make this file impossible to tree-shake.
 */
let hasWarnedMissingHost = false

/**
 * The single place Soketi connection options are written. Every real-time
 * component builds its client here so no component can drift back onto
 * Pusher Cloud.
 *
 * Returns a NEW client per call — deliberately not a singleton, because pusher-js
 * caches Channel objects per client and several components subscribe to the same
 * channel name. See docs/adr/0001-pusher-client-factory-not-singleton.md
 *
 * Callers own the lifecycle: unbind, unsubscribe and `disconnect()` on cleanup.
 *
 * Shares its shape with `lib/pusher-client.ts` in hms-id-fe-host (IDH-1621);
 * only the env prefix differs, because CRA and Next.js disagree about that.
 *
 * @param label optional tag for connection lifecycle logs, e.g. 'InpatientDocs'
 */
export const createPusherClient = (label?: string): Pusher => {
  const wsHost = process.env.REACT_APP_API_PUSHER_HOSTNAME
  const port = resolvePort()

  if (!wsHost && !hasWarnedMissingHost) {
    hasWarnedMissingHost = true
    console.error(
      '[pusherClient] REACT_APP_API_PUSHER_HOSTNAME is not set — falling back to Pusher Cloud. ' +
        'Real-time updates will NOT work.'
    )
  }

  const pusher = new Pusher(process.env.REACT_APP_API_PUSHER_KEY ?? '', {
    wsHost,
    wsPort: port,
    wssPort: port,
    forceTLS: true,
    disableStats: true,
    enabledTransports: ['ws', 'wss'],
    // Required by pusher-js alongside wsHost — do not remove. See ADR-0001.
    cluster: process.env.REACT_APP_API_PUSHER_CLUSTER || DEFAULT_CLUSTER,
  })

  const tag = label ? `[Pusher:${label}]` : '[Pusher]'

  pusher.connection.bind('connected', () =>
    console.log(`${tag} ✅ Connected. Socket ID:`, pusher.connection.socket_id)
  )
  pusher.connection.bind('error', (err: unknown) =>
    console.error(`${tag} ❌ Connection error:`, err)
  )
  pusher.connection.bind('failed', () => console.error(`${tag} ❌ Connection failed`))
  pusher.connection.bind('disconnected', () => console.warn(`${tag} ⚠️ Disconnected`))
  pusher.connection.bind('unavailable', () => console.error(`${tag} ❌ Service unavailable`))

  return pusher
}
